import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Perfil {
  id: string;
  user_id: string;
  nombre: string;
  email: string;
  rol: string;
  estado?: string;
  empresa_id?: string;
  avatar_url?: string;
  created_at?: string;
  updated_at?: string;
}

interface Empresa {
  id: string;
  nombre: string;
  razon_social?: string | null;
  nif_cif?: string | null;
  direccion?: string | null;
  direccion_calle?: string | null;
  direccion_numero?: string | null;
  codigo_postal?: string | null;
  ciudad?: string | null;
  provincia?: string | null;
  pais?: string | null;
  telefono?: string | null;
  email?: string | null;
  email_facturacion?: string | null;
  telefono_facturacion?: string | null;
  logo_url?: string | null;
  estado: string;
  plan_suscripcion?: string | null;
  fecha_inicio_suscripcion?: string | null;
  fecha_fin_suscripcion?: string | null;
  limite_usuarios?: number | null;
  limite_clientes?: number | null;
  configuracion?: any;
}

type AppRole = 'super_admin' | 'admin' | 'tecnico' | 'gestor' | 'cliente_portal';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  perfil: Perfil | null;
  empresa: Empresa | null;
  roles: AppRole[];
  usuario_actual: string | null;
  isLoading: boolean;
  hasRole: (role: AppRole) => boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, nombre: string, rol: 'admin' | 'tecnico' | 'gestor') => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  updatePerfil: (perfil: Partial<Perfil>) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [usuario_actual, setUsuarioActual] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  console.log('AuthProvider: Initializing with user:', user?.id, 'loading:', isLoading);

  const fetchPerfil = async (userId: string) => {
    console.log('AuthProvider: Fetching perfil for user:', userId);
    try {
      // 1. Cargar roles primero — usuarios del portal NO tienen fila en perfiles
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      const userRoles = (rolesData ?? []).map(r => r.role as AppRole);
      setRoles(userRoles);

      // 2. Si es usuario del portal cliente, cargar usuarios_portal_cliente
      if (userRoles.includes('cliente_portal')) {
        const { data: portalUser, error: pErr } = await (supabase as any)
          .from('usuarios_portal_cliente')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();

        if (pErr || !portalUser) {
          console.error('AuthProvider: Portal user no encontrado', pErr);
          return null;
        }

        // Cargar empresa para branding
        if (portalUser.empresa_id) {
          const { data: empresaData } = await supabase
            .from('empresas')
            .select('*')
            .eq('id', portalUser.empresa_id)
            .maybeSingle();
          if (empresaData) setEmpresa(empresaData as any);
        }

        // Marcar último acceso (best-effort)
        (supabase as any)
          .from('usuarios_portal_cliente')
          .update({ ultimo_acceso: new Date().toISOString() })
          .eq('user_id', userId)
          .then(() => {});

        return {
          id: portalUser.id,
          user_id: portalUser.user_id,
          nombre: portalUser.nombre,
          email: portalUser.email,
          rol: 'cliente_portal',
          empresa_id: portalUser.empresa_id,
        } as Perfil;
      }

      // 3. Usuario interno: cargar perfiles
      const { data: perfilData, error: perfilError } = await supabase
        .from('perfiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (perfilError) {
        console.error('AuthProvider: Error fetching perfil:', perfilError);
        toast({
          title: "Error de autenticación",
          description: "No se pudo cargar el perfil del usuario",
          variant: "destructive",
        });
        return null;
      }

      // Fetch empresa
      if (perfilData.empresa_id) {
        const { data: empresaData, error: empresaError } = await supabase
          .from('empresas')
          .select('*')
          .eq('id', perfilData.empresa_id)
          .single();

        if (!empresaError && empresaData) {
          setEmpresa(empresaData);
          if (empresaData.estado === 'suspendida' || empresaData.estado === 'cancelada') {
            console.warn('Empresa suspended or cancelled');
            await signOut();
            return null;
          }
        }
      }

      return perfilData;
    } catch (error) {
      console.error('AuthProvider: Error in fetchPerfil:', error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          setUsuarioActual(session.user.id);
          // Fetch user profile using setTimeout to avoid deadlock
          setTimeout(async () => {
            const userPerfil = await fetchPerfil(session.user.id);
            setPerfil(userPerfil as Perfil);
            setIsLoading(false);
          }, 0);
        } else {
          setUsuarioActual(null);
          setPerfil(null);
          setIsLoading(false);
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        setUsuarioActual(session.user.id);
        fetchPerfil(session.user.id).then((userPerfil) => {
          setPerfil(userPerfil as Perfil);
          setIsLoading(false);
        });
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        toast({
          title: "Error de autenticación",
          description: "Credenciales incorrectas. Por favor, verifica tu email y contraseña.",
          variant: "destructive",
        });
      }

      return { error };
    } catch (error) {
      toast({
        title: "Error",
        description: "Ha ocurrido un error inesperado.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string, nombre: string, rol: 'admin' | 'tecnico' | 'gestor') => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            nombre,
            rol
          }
        }
      });

      if (error) {
        toast({
          title: "Error al crear usuario",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Usuario creado",
          description: "El usuario ha sido creado exitosamente.",
        });
      }

      return { error };
    } catch (error) {
      toast({
        title: "Error",
        description: "Ha ocurrido un error inesperado.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Import offlineStorage dynamically to avoid circular dependency
      const { offlineStorage } = await import('@/lib/offlineStorage');
      
      await supabase.auth.signOut();
      setUser(null);
      setSession(null);
      setPerfil(null);
      setEmpresa(null);
      setRoles([]);
      setUsuarioActual(null);
      
      // Only clear offline data on explicit logout
      offlineStorage.clearAllOfflineData();
      
      toast({
        title: "Sesión cerrada",
        description: "Has cerrado sesión exitosamente.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al cerrar sesión.",
        variant: "destructive",
      });
    }
  };

  const updatePerfil = async (updatedPerfil: Partial<Perfil>) => {
    if (!perfil) return { error: 'No profile found' };

    try {
      const { error } = await supabase
        .from('perfiles')
        .update(updatedPerfil)
        .eq('id', perfil.id);

      if (!error && perfil) {
        setPerfil({ ...perfil, ...updatedPerfil });
        toast({
          title: "Perfil actualizado",
          description: "Los cambios han sido guardados.",
        });
      }

      return { error };
    } catch (error) {
      toast({
        title: "Error",
        description: "Error al actualizar el perfil.",
        variant: "destructive",
      });
      return { error };
    }
  };

  const hasRole = (role: AppRole): boolean => {
    return roles.includes(role);
  };

  const value: AuthContextType = {
    user,
    session,
    perfil,
    empresa,
    roles,
    usuario_actual,
    isLoading,
    hasRole,
    signIn,
    signUp,
    signOut,
    updatePerfil,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};